import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { OptionsListContainer } from '../Styles';
import { DraggableOption } from './DraggableOption';
import { Option } from './types';

interface OptionsListProps {
  selectedOptions: Option[];
  setSelectedOptions: (options: Option[]) => void;
  handleEditOption: (option: Option, isSoldOut?: boolean) => void;
  handleOptionRemove: (optionId: number | string) => void;
  optionsBorderColor: string;
  selectedItem: number | null;
  soldOutLoading?: number | string | null;
  onOrderChange?: (draggedOption: Option, fromIndex: number, toIndex: number) => void | Promise<void>;
}

export const OptionsList: React.FC<OptionsListProps> = ({
  selectedOptions,
  setSelectedOptions,
  handleEditOption,
  handleOptionRemove,
  optionsBorderColor,
  selectedItem,
  soldOutLoading,
  onOrderChange,
}) => {
  const [optionOrder, setOptionOrder] = useState<(number | string)[]>([]);

  useEffect(() => {
    const sorted = [...selectedOptions]
      .filter((o) => o?.id)
      .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    setOptionOrder(sorted.map((o) => o.id));
  }, [selectedOptions]);

  const handleDragEnd = useCallback(async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const newOrder = Array.from(optionOrder);
    const [moved] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, moved);
    setOptionOrder(newOrder);

    if (!onOrderChange) return;

    const draggedOption = selectedOptions.find((o) => o.id === moved);
    if (!draggedOption) return;

    await onOrderChange(draggedOption, source.index, destination.index);
  }, [optionOrder, selectedOptions, onOrderChange]);

  const orderedOptions = useMemo(() => {
    return optionOrder
      .map((id) => selectedOptions.find((o) => o.id === id))
      .filter(Boolean) as Option[];
  }, [optionOrder, selectedOptions]);

  return (
    <OptionsListContainer>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="options-list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {orderedOptions.map((option, index) => (
                <Draggable
                  key={String(option.id)}
                  draggableId={String(option.id)}
                  index={index}
                >
                  {(draggableProvided) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...draggableProvided.dragHandleProps}
                    >
                      <DraggableOption
                        option={option}
                        value={option.id}
                        handleEditOption={handleEditOption}
                        handleOptionRemove={handleOptionRemove}
                        optionsBorderColor={optionsBorderColor}
                        soldOutLoading={soldOutLoading}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </OptionsListContainer>
  );
};
